import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@material-ui/lab';
import { CircularProgress, Button, Grid, Hidden } from '@material-ui/core';
import clsx from 'clsx';

import { getRequest } from 'utils/api';
import { openModal } from 'features/utilsSlice';
import { getGigOwner } from 'utils/helpers';
import { setLastGigId, getLastGigId, setPreviousGigId, getPreviousGigId } from 'utils/localStorage';
import useIsBuyer from 'hooks/useIsBuyer';
import useCurrentProfile from 'hooks/useCurrentProfile';
import useIsAdvertiser from 'hooks/useIsAdvertiser';
import useIsInfluencer from 'hooks/useIsInfluencer';
import useIsSeller from 'hooks/useIsSeller';
import MiniWallet from './MiniWallet';
import Header from './agency/Header';
import ToDos from './todos/ToDos';
import AffiliateDialog from './AffiliateDialog';
import WishOrderCard from './wishlist/WishOrderCard';
import UnfundedGigTodos from './todos/UnfundedGigToDos';
import InventoryEditDialog from './InventoryEditDialog';
import EarningsCard from './profile/EarningsCard';
import CustomHidden from './CustomHidden';
import ReconnectOnlyFansC4Alert from './onlyFansC4Verification/ReconnectOnlyFansC4Alert';
import OnlyFansC4ConnectionsBtn from './onlyFansC4Verification/OnlyFansConnectionsBtn';
import OnlyFansC4Dialog from './onlyFansC4Verification/OnlyFansC4Dialog';
import DuplicateAlert from 'components/DuplicateAlert';
import ProfileCard from 'components/profile/ProfileCard';
import PurpleCard from 'components/PurpleCard';
import Gig from 'components/gigs/Gig';
import AnnouncementAlert from 'components/AnnouncementAlert';
import gigStyles from 'styles/components/gigs/Gig.module.scss';
import styles from 'styles/components/Dashboard.module.scss';

const progressStyle = { marginTop: 20 };
const noPaddingRight = { paddingRight: 0 };
const gigInvitesLength = 5;

const Dashboard = () => {
  const [gigInvitations, setGigInvitations] = useState([]);
  const [upcomingGigs, setUpcomingGigs] = useState([]);
  const [isLoading, setIsLoading] = useState({});
  const [hasLastGig, setHasLastGig] = useState(false);
  const [completedWishes, setCompletedWishes] = useState([]);

  const dispatch = useDispatch();
  const router = useRouter();
  const isBuyer = useIsBuyer();
  const isSeller = useIsSeller();

  const currentUser = useSelector(state => state.auth.currentUser);
  const isAdvertiser = useIsAdvertiser();
  const isInfluencer = useIsInfluencer();
  const profile = useCurrentProfile();

  const showToAdvertiser = isAdvertiser && !currentUser.avatar_url;
  const showToInfluencer = isInfluencer && !profile?.completed && profile?.adult;
  const showCard = currentUser?.confirmed && (showToAdvertiser || showToInfluencer);
  const showTraitsCard =
    currentUser?.confirmed &&
    (!profile?.trait_ids || profile?.trait_ids?.length === 0) &&
    isInfluencer &&
    profile?.adult &&
    !showCard;

  const lastGig =
    upcomingGigs.length > 0
      ? upcomingGigs.sort((a, b) => new Date(b.accepted_at) - new Date(a.accepted_at))[0]
      : null;

  const isGigOwner = getGigOwner(currentUser, lastGig?.user_id);
  const hasWishes = completedWishes.length > 0 && !isLoading.completedWishes;

  const dismissDuplicateAlert = () => {
    setLastGigId(0, 'active');
    setPreviousGigId(lastGig.id, 'active');
    setHasLastGig(false);
  };

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, invitations: true }));

    getRequest({
      endpoint: `gig_invitations`,
      query: {
        status: 'pending',
        per_page: 6,
        private: true,
      },
    })
      .then(invitations => {
        setGigInvitations(invitations.map(inv => inv.gig));
        setIsLoading(prevState => ({ ...prevState, invitations: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, invitations: false })));
  }, [isAdvertiser, profile?.id, currentUser.id, currentUser.primary_interest]);

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, upcoming: true }));

    getRequest({
      endpoint: 'gigs/upcoming',
    })
      .then(gigs => {
        if (isBuyer) {
          setUpcomingGigs(gigs);
        } else {
          setUpcomingGigs(gigs.filter(g => g.funded));
        }
        setIsLoading(prevState => ({ ...prevState, upcoming: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, upcoming: false })));
  }, [isBuyer, currentUser.id, currentUser.primary_interest]);

  useEffect(() => {
    if (!lastGig || !getGigOwner(currentUser, lastGig.user_id)) return;

    const lastGigId = +getLastGigId('active');
    const previousGigId = +getPreviousGigId('active');

    if (lastGig.id === lastGigId) setHasLastGig(true);

    if (lastGig.id !== lastGigId && lastGig.id !== previousGigId) {
      setPreviousGigId(lastGig.id, 'active');
      setLastGigId(lastGig.id, 'active');
    }
  }, [lastGig, currentUser]);

  useEffect(() => {
    if (!isBuyer) return;

    setIsLoading(prevState => ({ ...prevState, completedWishes: true }));

    getRequest({
      endpoint: 'users/wish_orders',
    })
      .then(orders => {
        setCompletedWishes(orders);
        setIsLoading(prevState => ({ ...prevState, completedWishes: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, completedWishes: false })));
  }, [isBuyer]);

  return (
    <div className={styles.dashboardContainer}>
      <Hidden smDown>
        <Header title="Dashboard" />
      </Hidden>

      <AnnouncementAlert />

      <ReconnectOnlyFansC4Alert />

      <div className={clsx(gigStyles.gigContainer, styles.container)}>
        {isBuyer && (
          <div className={styles.buttons}>
            <div className={clsx(styles.outlined, { [styles.largerBtn]: !isBuyer })}>
              <Link
                href={isAdvertiser && !currentUser?.agency && isBuyer ? '/hire' : '/discover'}
                passHref
              >
                <Button
                  color="primary"
                  variant="outlined"
                  className="mobile-outlined-button"
                  fullWidth
                >
                  Discover
                </Button>
              </Link>
            </div>

            <div className={styles.contained}>
              <Link href={isBuyer ? '/gigs/new' : '/gigs/active'} passHref>
                <Button color="primary" variant="contained" fullWidth>
                  {isBuyer ? 'Post an offer' : 'My gigs'}
                </Button>
              </Link>
            </div>

            <Hidden smDown>
              <OnlyFansC4ConnectionsBtn />
            </Hidden>
          </div>
        )}

        {isSeller && (
          <Hidden smDown>
            <div className={styles.buttons}>
              <OnlyFansC4ConnectionsBtn />
            </div>
          </Hidden>
        )}

        {isBuyer && (
          <Hidden mdUp>
            <div className="divider" />
          </Hidden>
        )}

        <Grid container>
          <Grid
            item
            xs={12}
            lg={4}
            className={clsx(gigStyles.container, styles.item)}
            style={isSeller ? { paddingTop: 0 } : {}}
          >
            <Hidden mdUp>
              <div
                className={styles.title}
                style={{
                  marginTop: isBuyer ? 20 : 0,
                }}
              >
                <h4>DASHBOARD</h4>

                <OnlyFansC4ConnectionsBtn />
              </div>
            </Hidden>

            <ProfileCard />

            {isSeller && <EarningsCard />}

            {showCard && (
              <PurpleCard
                title="Get full access"
                description={
                  isAdvertiser
                    ? 'Complete your profile to be matched with the world’s largest marketplace of influencers.'
                    : 'Complete your account setup to get matched with paying brands and gigs.'
                }
                buttonText={isAdvertiser ? 'Complete profile' : 'Setup and verify my socials'}
                handleClick={() => {
                  if (isAdvertiser) {
                    dispatch(openModal({ name: 'Edit Advertiser Profile' }));
                    router.push(`/c/${currentUser.username}`);
                  } else {
                    router.push('/inventory');
                  }
                }}
              />
            )}

            {showTraitsCard && (
              <PurpleCard
                title="Add your traits"
                description="Update your profile to be matched with the world’s largest marketplace of influencers."
                buttonText="Setup my traits"
                handleClick={() =>
                  dispatch(openModal({ name: 'Inventory Edit', data: { step: 'Traits' } }))
                }
              />
            )}

            {!currentUser.confirmed && (
              <PurpleCard
                title="Confirm your account"
                description={`Confirm your account to be matched with the world’s largest marketplace of ${
                  isAdvertiser ? 'influencers' : 'advertisers'
                }.`}
                buttonText="CONFIRM ACCOUNT"
                handleClick={() => router.push('/access-confirmation')}
              />
            )}

            <CustomHidden down={1440}>
              <MiniWallet />
            </CustomHidden>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            className={clsx(gigStyles.container, styles.item, {
              [styles.order]: !isLoading.invitations && gigInvitations.length > 0,
            })}
          >
            {hasLastGig && lastGig?.public && isGigOwner && (
              <DuplicateAlert dismissAlert={dismissDuplicateAlert} gigId={lastGig.id} />
            )}

            {isGigOwner && <UnfundedGigTodos />}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <h4>ORDERS</h4>

              {gigInvitations.length > 5 && (
                <span onClick={() => router.push('/offers')} className={styles.viewMore}>
                  View all
                </span>
              )}
            </div>

            {isLoading.invitations && <CircularProgress style={progressStyle} color="primary" />}

            {gigInvitations.length === 0 && !isLoading.invitations && (
              <Alert severity="info">No orders</Alert>
            )}

            {gigInvitations.length > 0 && !isLoading.invitations && (
              <>
                {gigInvitations
                  .slice(0, gigInvitesLength)
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(gig => (
                    <Gig key={gig.id} gig={gig} isOffer rating />
                  ))}
              </>
            )}

            {isBuyer && isInfluencer && hasWishes && (
              <div style={{ marginTop: 20 }}>
                <h4>GRANTED WISHES</h4>

                {completedWishes
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(order => (
                    <WishOrderCard key={order.id} order={order} userKey="influencer" />
                  ))}
              </div>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            className={clsx(gigStyles.container, styles.item)}
            style={noPaddingRight}
          >
            <ToDos />
          </Grid>
        </Grid>
      </div>

      <AffiliateDialog />
      <InventoryEditDialog />
    </div>
  );
};

export default Dashboard;
